import { useState, useCallback, useRef, useEffect } from 'react';
import type { StationsResponse, Station } from '@/types';
import { apiClient, ApiClientError } from '@/api/client';

interface UseStationsParams {
  lat: number;
  lng: number;
  radius: number;
  enabled: boolean;
}

interface UseStationsResult {
  stations: Station[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  dataTimestamp: string | null;
  isStale: boolean;
  meta: StationsResponse['meta'] | null;
}

const DEBOUNCE_MS = 100;

// Transform API stations to add convenience latitude/longitude properties
function transformStations(apiStations: Station[]): Station[] {
  return apiStations.map((station) => ({
    ...station,
    latitude: station.location.lat,
    longitude: station.location.lng,
  }));
}

export function useStations({
  lat,
  lng,
  radius,
  enabled,
}: UseStationsParams): UseStationsResult {
  const [stations, setStations] = useState<Station[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isStale, setIsStale] = useState(false);
  const [meta, setMeta] = useState<StationsResponse['meta'] | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastParamsRef = useRef<string>('');

  const fetchStations = useCallback(async (latitude: number, longitude: number, searchRadius: number) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.getStations({ latitude, longitude }, searchRadius);
      setStations(transformStations(response.stations));
      setMeta(response.meta);
      setIsStale(response._isStale ?? false);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(new Error(err.message));
      } else if (err instanceof Error) {
        setError(err);
      } else {
        setError(new Error('Failed to fetch stations'));
      }
      console.error('Error fetching stations:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refetch = useCallback(() => {
    if (enabled) {
      void fetchStations(lat, lng, radius);
    }
  }, [lat, lng, radius, enabled, fetchStations]);

  useEffect(() => {
    // Clear any pending debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Don't fetch if not enabled
    if (!enabled) {
      return;
    }

    // Check if params actually changed
    const paramsKey = `${lat},${lng},${radius}`;
    if (paramsKey === lastParamsRef.current) {
      return;
    }

    // Debounce the fetch
    debounceRef.current = setTimeout(() => {
      lastParamsRef.current = paramsKey;
      void fetchStations(lat, lng, radius);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [lat, lng, radius, enabled, fetchStations]);

  return {
    stations,
    isLoading,
    error,
    refetch,
    dataTimestamp: meta?.dataTimestamp ?? null,
    isStale,
    meta,
  };
}
